import type { PropertyFormValues } from '../validations/property-validations';
import type { PropertyLocationValues } from '../types/property-location-fields.type';
import type { LocationAutocompleteSuggestion } from '../../shared/types/location/geocoding.type';

/** Fields that must move together when replacing a property's location. */
export const propertyLocationFieldNames = [
  'countryCode',
  'addressDisplay',
  'streetName',
  'streetNumber',
  'unit',
  'neighborhood',
  'locality',
  'region',
  'latitude',
  'longitude',
] as const satisfies readonly (keyof PropertyLocationValues)[];

const formatCoordinate = (value: number): string =>
  Number(value.toFixed(6)).toString();

/** Replaces location fields together, avoiding details inherited from an old address. */
export const buildPropertyLocationPatchFromSuggestion = (
  suggestion: LocationAutocompleteSuggestion,
  currentValues: PropertyLocationValues
): PropertyLocationValues => {
  const countryCode = suggestion.countryCode?.trim().toUpperCase() ?? '';
  const streetName = suggestion.streetName ?? '';
  const streetNumber = suggestion.streetNumber ?? '';
  const sameBuilding =
    Boolean(countryCode && streetName && streetNumber) &&
    countryCode === currentValues.countryCode &&
    streetName === currentValues.streetName &&
    streetNumber === currentValues.streetNumber;
  const segments = [
    streetName,
    suggestion.neighborhood,
    suggestion.locality,
    suggestion.region,
  ]
    .map((value) => value?.trim())
    .filter(Boolean);

  return {
    countryCode,
    addressDisplay:
      Array.from(new Set(segments)).join(', ') || suggestion.label,
    streetName,
    streetNumber,
    unit: sameBuilding ? currentValues.unit ?? '' : '',
    neighborhood: suggestion.neighborhood ?? '',
    locality: suggestion.locality ?? '',
    region: suggestion.region ?? '',
    latitude: formatCoordinate(suggestion.latitude),
    longitude: formatCoordinate(suggestion.longitude),
  };
};

/** Formats map coordinates for the property form. */
export const buildPropertyCoordinatePatch = (params: {
  /** Latitude in decimal degrees. */
  latitude: number;
  /** Longitude in decimal degrees. */
  longitude: number;
}): Pick<PropertyFormValues, 'latitude' | 'longitude'> => ({
  latitude: formatCoordinate(params.latitude),
  longitude: formatCoordinate(params.longitude),
});
